import * as Sentry from '@sentry/react';

let sentryInitialized = false;

/**
 * 初始化 Sentry（僅在非 development 且已設定 VITE_SENTRY_DSN 時）
 */
export function initSentry(): void {
  if (import.meta.env.MODE === 'development') {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  if (sentryInitialized) {
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE || 'production',
      tracesSampleRate: 1.0,
      release: import.meta.env.VITE_GIT_SHA || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      beforeSend(event) {
        if (event.request?.headers) {
          delete event.request.headers.Authorization;
        }
        if (event.user?.id) {
          const id = String(event.user.id);
          event.user.id = id.length > 8 ? id.substring(0, 8) + '...' : id;
        }
        return event;
      },
    });
    sentryInitialized = true;
  } catch {
    // 不影響應用程式
  }
}

/**
 * 捕獲異常並送往 Sentry（已初始化時）
 */
export function captureException(
  error: unknown,
  context?: {
    level?: 'error' | 'warning' | 'info';
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    user?: { id?: string; tenantId?: string };
  }
): void {
  if (!sentryInitialized) return;
  try {
    Sentry.withScope(scope => {
      if (context?.level) scope.setLevel(context.level as Sentry.SeverityLevel);
      if (context?.tags) {
        Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
      }
      if (context?.extra) {
        Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v));
      }
      if (context?.user) scope.setUser(context.user);
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(String(error), context?.level || 'error');
      }
    });
  } catch {
    // 忽略
  }
}
